import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
    Menu,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import axios from "../../../utils/axiosInstance";
import React, { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import SearchIcon from "@mui/icons-material/Search";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { formatAvailsPacificDateTime } from "../../../utilities/helpers/formatUnix";
import { AVAILS_PLUS_MANAGEMENT } from "../LandingPage/constants";
import AvailsPlusCatalogTable, {
    AvailsPlusRow,
    EditableField,
} from "./AvailsPlusCatalogTable";
import AvailsPlusDetailDrawer from "./AvailsPlusDetailDrawer";
import LicenseWindowRangeField from "./LicenseWindowRangeField";
import { RokuStatus } from "./RokuIndicator";
import {
    appendColumnFiltersToSearchParams,
    appendColumnFiltersToRecord,
    ColumnFiltersState,
    FILTERABLE_COLUMN_CONFIG,
    FILTERABLE_COLUMN_LABELS,
    FilterableColumnKey,
    getColumnFilterSummary,
    hasActiveColumnFilters,
    isColumnFilterActive,
    rowMatchesActiveView,
} from "./availsPlusFilterConfig";
import {
    AvailsPlusAirtableSourceKey,
    DEFAULT_AVAILS_PLUS_AIRTABLE_SOURCE,
    getAvailsPlusAirtableSourceLabel,
    isAvailsPlusAirtableSourceKey,
} from "./availsPlusAirtableSources";
import AirtableSourceSelect from "./AirtableSourceSelect";
import AvailsSummaryWidgets, {
    AvailsPlusSummaryData,
    AvailsWidgetSelection,
} from "./AvailsSummaryWidgets";
import {
    formatSlateSubmitError,
    SlateMismatch,
    SlateSyncMeta,
    summarizeSlateSubmitResults,
} from "./slateUtils";
import { SlateStatus } from "./SlateIndicator";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
} from "../../../utils/permissions";

const BASE = process.env.BASE_URL || "";
const SEARCH_URL = `${BASE}api/v1/cms/avails-plus/search`;
const SUMMARY_URL = `${BASE}api/v1/cms/avails-plus/summary`;
const UPDATE_URL = `${BASE}api/v1/cms/avails-plus/catalog`;
const PREVIEW_URL = `${BASE}api/v1/cms/avails-plus/preview/airtable`;
const IMPORT_URL = `${BASE}api/v1/cms/avails-plus/import/canvas`;
const SLATE_SYNC_STATUS_URL = `${BASE}api/v1/cms/avails-plus/slate/sync-status`;
const SLATE_SUBMIT_URL = `${BASE}api/v1/cms/avails-plus/slate/submit`;
const ROKU_SUBMIT_URL = `${BASE}api/v1/cms/avails-plus/roku/submit`;
const EXPORT_SPREADSHEET_URL = `${BASE}api/v1/cms/avails-plus/export/spreadsheet`;

const MAX_RANGE_MONTHS = 6;
const PREVIEW_ROWS_PER_PAGE = 25;
const PREVIEW_ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
const SLATE_SUBMIT_MAX_REF_IDS = 100;
const ROKU_SUBMIT_MAX_REF_IDS = 100;
const ROKU_EXPORT_TIMEOUT_MS = 300000;
const AVAILS_PACIFIC_TZ = "America/Los_Angeles";

function pacificMonthYearDisplay(date = new Date()): string {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: AVAILS_PACIFIC_TZ,
        month: "numeric",
        year: "numeric",
    }).formatToParts(date);
    const month = parts.find((part) => part.type === "month")?.value || "";
    const year = parts.find((part) => part.type === "year")?.value || "";
    return `${month}/${year}`;
}

function utcTodayYmd(): string {
    return new Date().toISOString().slice(0, 10);
}

function addUtcDaysYmd(ymd: string, days: number): string {
    const date = new Date(`${ymd}T12:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}

function appendWidgetFiltersToSearchParams(
    params: URLSearchParams,
    selection: AvailsWidgetSelection
): void {
    if (!selection) return;
    if (selection.type === "overall") {
        params.set("slateStatus", selection.slateStatus);
        return;
    }
    if (selection.type === "roku") {
        params.set("rokuStatus", selection.rokuStatus);
        return;
    }
    if (selection.type === "catalog") {
        params.set("licenseWindowStatus", selection.status);
        return;
    }
    if (selection.type === "syncRequired") {
        const today = utcTodayYmd();
        params.set("syncRequired", "true");
        params.set("dateFrom", today);
        params.set("dateTo", addUtcDaysYmd(today, selection.days));
        return;
    }
    const today = utcTodayYmd();
    if (selection.bucket === "recentlyExpired") {
        params.set("latestEndDateFrom", addUtcDaysYmd(today, -14));
        params.set("latestEndDateTo", today);
        return;
    }
    params.set("latestEndDateFrom", today);
    params.set("latestEndDateTo", addUtcDaysYmd(today, 14));
}

function appendWidgetFiltersToRecord(
    body: Record<string, unknown>,
    selection: AvailsWidgetSelection
): void {
    if (!selection) return;
    if (selection.type === "overall") {
        body.slateStatus = selection.slateStatus;
        return;
    }
    if (selection.type === "roku") {
        body.rokuStatus = selection.rokuStatus;
        return;
    }
    if (selection.type === "catalog") {
        body.licenseWindowStatus = selection.status;
        return;
    }
    if (selection.type === "syncRequired") {
        const today = utcTodayYmd();
        body.syncRequired = "true";
        body.dateFrom = today;
        body.dateTo = addUtcDaysYmd(today, selection.days);
        return;
    }
    const today = utcTodayYmd();
    if (selection.bucket === "recentlyExpired") {
        body.latestEndDateFrom = addUtcDaysYmd(today, -14);
        body.latestEndDateTo = today;
        return;
    }
    body.latestEndDateFrom = today;
    body.latestEndDateTo = addUtcDaysYmd(today, 14);
}

function isRequestCanceled(error: unknown): boolean {
    return (
        (error instanceof Error && error.name === "CanceledError") ||
        (typeof error === "object" &&
            error !== null &&
            "code" in error &&
            (error as { code: string }).code === "ERR_CANCELED")
    );
}

const SNACKBAR_ANCHOR = {
    vertical: "bottom" as const,
    horizontal: "right" as const,
};

type SortOrder = "asc" | "desc" | null;

interface AirtablePreviewRow {
    refId: string;
    name: string;
    contentType: string;
    startDate: string | number;
    endDate: string | number;
    comingSoonDate: string | number;
    licensor: string;
    status: "new" | "update";
    windowAction: string;
}

interface ImportCounts {
    movies: number;
    episodes: number;
    supplementals: number;
}

const WINDOW_ACTION_LABELS: Record<string, string> = {
    new_window: "New window",
    exact: "No update required",
    extension: "Extension",
    delay: "Delay",
    slide: "Slide",
    consecutive: "Consecutive window",
};

function coerceAvailsDateValue(
    raw: Record<string, unknown>,
    ...keys: string[]
): string | number {
    for (const key of keys) {
        const value = raw[key];
        if (value === null || value === undefined || value === "") continue;
        if (typeof value === "number" && Number.isFinite(value)) return value;
        const text = String(value).trim();
        if (text) return text;
    }
    return "";
}

function validateFilterDateRange(
    from: Dayjs | null,
    to: Dayjs | null
): string | null {
    if (from?.isValid() && to?.isValid() && to.isBefore(from, "day")) {
        return "End date cannot be before the start date.";
    }
    return null;
}

function getDownloadFilenameFromHeaders(
    headers: Record<string, string>
): string | null {
    const disposition = headers["content-disposition"];
    if (typeof disposition !== "string") return null;
    const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const match = regex.exec(disposition);
    if (match?.[1]) {
        return match[1].replace(/['"]/g, "").trim();
    }
    return null;
}

function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

type AvailsExportErrorBody = {
    message?: string;
    data?: { invalidIds?: Array<{ referenceId?: string; field?: string; value?: string }> };
};

function formatExportErrorBody(parsed: AvailsExportErrorBody): string | null {
    const message = parsed.message ? String(parsed.message) : "";
    const invalidIds = parsed.data?.invalidIds;
    if (Array.isArray(invalidIds) && invalidIds.length) {
        const preview = invalidIds
            .slice(0, 5)
            .map((item) => item.referenceId || item.value || item.field)
            .filter(Boolean)
            .join(", ");
        const extra = invalidIds.length > 5 ? ` (+${invalidIds.length - 5} more)` : "";
        return `${message}${preview ? ` ${preview}${extra}` : ""}`.trim() || null;
    }
    return message || null;
}

async function readExportErrorMessage(error: unknown): Promise<string | null> {
    if (
        typeof error !== "object" ||
        error === null ||
        !("response" in error) ||
        !(error as { response?: { data?: unknown } }).response?.data
    ) {
        return null;
    }
    const data = (error as { response: { data: unknown } }).response.data;
    if (data instanceof Blob) {
        try {
            const text = await data.text();
            const parsed = JSON.parse(text) as AvailsExportErrorBody;
            return formatExportErrorBody(parsed);
        } catch {
            return null;
        }
    }
    if (typeof data === "object" && data !== null) {
        return formatExportErrorBody(data as AvailsExportErrorBody);
    }
    return null;
}

function buildCatalogRowId(
    referenceId: string,
    windowIndex: number,
    playbackStart: string | number,
    playbackEnd: string | number
): string {
    const start = String(playbackStart ?? "");
    const end = String(playbackEnd ?? "");
    if (start || end) {
        return `${referenceId}#${windowIndex}#${start}#${end}`;
    }
    return `${referenceId}#${windowIndex}`;
}

function getCatalogRowKey(
    row: Pick<
        AvailsPlusRow,
        | "rowId"
        | "reference_id"
        | "window_index"
        | "playback_start_date"
        | "playback_end_date"
    >
): string {
    return (
        row.rowId ||
        buildCatalogRowId(
            row.reference_id,
            row.window_index,
            row.playback_start_date,
            row.playback_end_date
        )
    );
}

function refIdsFromRowKeys(rowKeys: Iterable<string>): string[] {
    return [
        ...new Set(
            [...rowKeys]
                .map((key) => key.split("#")[0]?.trim())
                .filter(Boolean)
        ),
    ];
}

function normalizeCatalogRow(raw: Record<string, unknown>): AvailsPlusRow {
    const slateStatus = raw.slate_status ?? raw.slateStatus;
    const rokuStatus = raw.roku_status ?? raw.rokuStatus;
    const referenceId = String(raw.reference_id ?? raw.referenceId ?? "");
    const windowIndex = Number(raw.window_index ?? raw.windowIndex ?? 0);
    const playbackStartDate = coerceAvailsDateValue(
        raw,
        "playback_start_date",
        "playbackStartDate"
    );
    const playbackEndDate = coerceAvailsDateValue(
        raw,
        "playback_end_date",
        "playbackEndDate"
    );
    return {
        rowId: buildCatalogRowId(
            referenceId,
            windowIndex,
            playbackStartDate,
            playbackEndDate
        ),
        window_index: windowIndex,
        version: (() => {
            const v = Number(raw.version);
            return Number.isFinite(v) ? v : 0;
        })(),
        slate_status:
            slateStatus === "synced" ||
            slateStatus === "require_sync" ||
            slateStatus === "not_exist"
                ? slateStatus
                : "not_exist",
        slate_mismatches: Array.isArray(raw.slate_mismatches)
            ? (raw.slate_mismatches as SlateMismatch[])
            : Array.isArray(raw.slateMismatches)
              ? (raw.slateMismatches as SlateMismatch[])
              : [],
        roku_status:
            rokuStatus === "synced" ||
            rokuStatus === "require_sync" ||
            rokuStatus === "not_exist"
                ? (rokuStatus as RokuStatus)
                : "not_exist",
        name: String(raw.name ?? ""),
        reference_id: referenceId,
        content_type: String(raw.content_type ?? raw.contentType ?? ""),
        license_type: String(raw.license_type ?? raw.licenseType ?? "SVOD"),
        format_profile: String(raw.format_profile ?? raw.formatProfile ?? "HD"),
        playback_start_date: playbackStartDate,
        playback_end_date: playbackEndDate,
        coming_soon_date: coerceAvailsDateValue(
            raw,
            "coming_soon_date",
            "comingSoonDate",
            "announce_date",
            "announceDate"
        ),
        branding_rights: String(raw.branding_rights ?? raw.brandingRights ?? ""),
        hdr: String(raw.hdr ?? ""),
        license_rights_description: String(
            raw.license_rights_description ?? raw.licenseRightsDescription ?? ""
        ),
        licensor: String(raw.licensor ?? raw.licensorStudio ?? ""),
        season_number: String(raw.season_number ?? raw.seasonNumber ?? ""),
        episode_number: String(raw.episode_number ?? raw.episodeNumber ?? ""),
        episode_count: String(raw.episode_count ?? raw.episodeCount ?? ""),
        season_count: String(raw.season_count ?? raw.seasonCount ?? ""),
        series_ref_id: String(raw.series_ref_id ?? raw.seriesRefId ?? ""),
        season_ref_id: String(raw.season_ref_id ?? raw.seasonRefId ?? ""),
        series_title: String(
            raw.series_title ?? raw.seriesTitle ?? raw.SeriesTitleInternalAlias ?? ""
        ),
        season_title: String(
            raw.season_title ?? raw.seasonTitle ?? raw.SeasonTitleInternalAlias ?? ""
        ),
        caption_included:
            raw.caption_included === false ||
            raw.captionIncluded === false ||
            raw.CaptionIncluded === false
                ? false
                : true,
    };
}

function AirtableLogoIcon() {
    return (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                width: 20,
                height: 20,
                mr: 0.5,
                "& svg": { width: "100%", height: "100%" },
            }}
        >
            <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
            >
                <path
                    d="M5 3h4v4H5V3zm0 6h4v4H5V9zm0 6h4v4H5v-4zm6-12h4v4h-4V3zm0 6h4v4h-4V9zm0 6h4v4h-4v-4zm6-12h4v4h-4V3zm0 6h4v4h-4V9zm0 6h4v4h-4v-4z"
                    fill="currentColor"
                />
            </svg>
        </Box>
    );
}

function normalizePreviewRow(raw: Record<string, unknown>): AirtablePreviewRow {
    const windowAction = String(raw.window_action ?? raw.windowAction ?? "new_window");
    return {
        refId: String(raw.refId ?? ""),
        name: String(raw.name ?? ""),
        contentType: String(raw.content_type ?? raw.contentType ?? ""),
        startDate: coerceAvailsDateValue(
            raw,
            "start_date",
            "startDate",
            "playback_start_date",
            "playbackStartDate"
        ),
        endDate: coerceAvailsDateValue(
            raw,
            "end_date",
            "endDate",
            "playback_end_date",
            "playbackEndDate"
        ),
        comingSoonDate: coerceAvailsDateValue(
            raw,
            "coming_soon_date",
            "comingSoonDate",
            "announce_date",
            "announceDate"
        ),
        licensor: String(raw.licensor ?? ""),
        status: raw.status === "update" ? "update" : "new",
        windowAction,
    };
}

function formatWindowActionLabel(action: string): string {
    return WINDOW_ACTION_LABELS[action] ?? action;
}

function formatImportToastMessage(counts: ImportCounts): string {
    const parts: string[] = [];
    if (counts.movies > 0) {
        parts.push(
            `${counts.movies} Movie${counts.movies === 1 ? "" : "s"}`
        );
    }
    if (counts.episodes > 0) {
        parts.push(
            `${counts.episodes} Episode${counts.episodes === 1 ? "" : "s"}`
        );
    }
    if (counts.supplementals > 0) {
        parts.push(
            `${counts.supplementals} Supplemental${counts.supplementals === 1 ? "" : "s"}`
        );
    }
    if (!parts.length) return "Import completed.";
    if (parts.length === 1) return `Imported ${parts[0]} Successfully`;
    if (parts.length === 2) {
        return `Imported ${parts[0]} and ${parts[1]} Successfully`;
    }
    return `Imported ${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]} Successfully`;
}

function TruncatedTableCell({
    value,
    align = "left",
    maxWidth,
}: {
    value?: string | null;
    align?: "left" | "center" | "right";
    maxWidth?: number | string;
}) {
    const display = value?.trim() ? value.trim() : "-";

    if (display === "-") {
        return (
            <TableCell align={align} sx={{ maxWidth }}>
                {display}
            </TableCell>
        );
    }

    return (
        <TableCell align={align} sx={{ maxWidth, overflow: "hidden" }}>
            <Tooltip title={display} arrow placement="top">
                <Box
                    component="span"
                    sx={{
                        display: "block",
                        maxWidth: maxWidth ?? "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {display}
                </Box>
            </Tooltip>
        </TableCell>
    );
}

const PreviewTableRow = memo(function PreviewTableRow({
    row,
}: {
    row: AirtablePreviewRow;
}) {
    return (
        <TableRow>
            <TruncatedTableCell value={row.refId} maxWidth={180} />
            <TruncatedTableCell value={row.name} maxWidth={240} />
            <TruncatedTableCell value={row.contentType} maxWidth={120} />
            <TableCell>
                {formatAvailsPacificDateTime(row.startDate)}
            </TableCell>
            <TableCell>
                {formatAvailsPacificDateTime(row.endDate)}
            </TableCell>
            <TruncatedTableCell value={row.licensor} maxWidth={140} />
            <TruncatedTableCell
                value={
                    row.windowAction === "exact"
                        ? "No update required"
                        : row.status === "update"
                          ? "Update"
                          : "New"
                }
                maxWidth={140}
            />
            <TruncatedTableCell
                value={formatWindowActionLabel(row.windowAction)}
                maxWidth={140}
            />
        </TableRow>
    );
});

const AvailsPlusManagement = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();
    const canViewCatalog = hasPermission(PERMISSIONS.mam, ACTION.VIEW);
    const canEditCatalog = hasPermission(PERMISSIONS.mam, ACTION.EDIT);
    const [initialLoading, setInitialLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(false);
    const fetchRequestIdRef = useRef(0);
    const abortControllerRef = useRef<AbortController | null>(null);
    const hasLoadedRef = useRef(false);
    const [searchKey, setSearchKey] = useState("");
    const [contentList, setContentList] = useState<AvailsPlusRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [skip, setSkip] = useState(AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP);
    const [rowsPerPage, setRowsPerPage] = useState(
        AVAILS_PLUS_MANAGEMENT.DEFAULT_LIMIT
    );
    const [noResult, setNoResult] = useState(false);
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<SortOrder>(null);
    const [filterDateFrom, setFilterDateFrom] = useState<Dayjs | null>(null);
    const [filterDateTo, setFilterDateTo] = useState<Dayjs | null>(null);
    const [filterDateError, setFilterDateError] = useState<string | null>(
        null
    );
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>({});
    const [fetchDialogOpen, setFetchDialogOpen] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [airtableStartDate, setAirtableStartDate] = useState<Dayjs | null>(
        null
    );
    const [airtableEndDate, setAirtableEndDate] = useState<Dayjs | null>(null);
    const [airtableSource, setAirtableSource] =
        useState<AvailsPlusAirtableSourceKey>(DEFAULT_AVAILS_PLUS_AIRTABLE_SOURCE);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [airtableLoading, setAirtableLoading] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const [previewRows, setPreviewRows] = useState<AirtablePreviewRow[]>([]);
    const [previewStartDate, setPreviewStartDate] = useState("");
    const [previewEndDate, setPreviewEndDate] = useState("");
    const [previewAirtableSource, setPreviewAirtableSource] =
        useState<AvailsPlusAirtableSourceKey>(DEFAULT_AVAILS_PLUS_AIRTABLE_SOURCE);
    const [previewPage, setPreviewPage] = useState(0);
    const [previewRowsPerPage, setPreviewRowsPerPage] = useState(
        PREVIEW_ROWS_PER_PAGE
    );
    const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
    const [detailRefId, setDetailRefId] = useState<string | null>(null);
    const activeDetailRef = useRef({ refId: detailRefId, airtableSource });
    useLayoutEffect(() => {
        activeDetailRef.current = { refId: detailRefId, airtableSource };
    }, [detailRefId, airtableSource]);
    const [detailSlateStatus, setDetailSlateStatus] = useState<SlateStatus>(null);
    const [detailSlateMismatches, setDetailSlateMismatches] = useState<SlateMismatch[]>([]);
    const [detailRokuStatus, setDetailRokuStatus] = useState<RokuStatus>(null);
    const [slateSyncEnabled, setSlateSyncEnabled] = useState(false);
    const [slatePutEnabled, setSlatePutEnabled] = useState(false);
    const showSubmitToSlateFab =
        canEditCatalog && slateSyncEnabled && slatePutEnabled;
    const [slateSyncMeta, setSlateSyncMeta] = useState<SlateSyncMeta | null>(null);
    const [slateRefreshing, setSlateRefreshing] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
    const [slateSubmitting, setSlateSubmitting] = useState(false);
    const [slateSyncingRefId, setSlateSyncingRefId] = useState<string | null>(null);
    const [slateSyncErrorByRefId, setSlateSyncErrorByRefId] = useState<
        Record<string, string>
    >({});
    const selectedRefIds = new Set(refIdsFromRowKeys(selectedRowKeys));
    const [exportLoading, setExportLoading] = useState(false);
    const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(null);
    const [rokuSubmitLoading, setRokuSubmitLoading] = useState(false);
    const [rokuSubmittingRefId, setRokuSubmittingRefId] = useState<string | null>(null);
    const [summary, setSummary] = useState<AvailsPlusSummaryData | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [widgetSelection, setWidgetSelection] =
        useState<AvailsWidgetSelection>(null);
    const airtableAutoSyncCurrentDisplay = pacificMonthYearDisplay();
    const summaryAbortRef = useRef<AbortController | null>(null);
    const summaryRequestIdRef = useRef(0);

    const clearRowSelection = () => {
        setSelectedRowKeys(new Set());
    };

    const getActiveViewFilterOptions = () => ({
        widgetSelection,
        filterDateFrom,
        filterDateTo,
        columnFilters,
    });

    const deselectRowKeys = (rowKeys: string[]) => {
        if (!rowKeys.length) return;
        setSelectedRowKeys((prev) => {
            const next = new Set(prev);
            for (const rowKey of rowKeys) next.delete(rowKey);
            return next.size === prev.size ? prev : next;
        });
    };

    const deselectRefIds = (refIds: string[]) => {
        if (!refIds.length) return;
        const refIdSet = new Set(refIds);
        setSelectedRowKeys((prev) => {
            const next = new Set(prev);
            for (const rowKey of prev) {
                const refId = rowKey.split("#")[0]?.trim();
                if (refId && refIdSet.has(refId)) next.delete(rowKey);
            }
            return next.size === prev.size ? prev : next;
        });
    };

    const applySlateStatusToRefIdInView = (
        refId: string,
        status: SlateStatus,
        mismatches: SlateMismatch[]
    ) => {
        const keysToDeselect: string[] = [];
        let removedCount = 0;
        const viewOptions = getActiveViewFilterOptions();

        setContentList((prev) => {
            const nextList = prev.flatMap((row) => {
                if (row.reference_id !== refId) return [row];
                const updated: AvailsPlusRow = {
                    ...row,
                    slate_status: status,
                    slate_mismatches: mismatches,
                };
                if (rowMatchesActiveView(updated, viewOptions)) return [updated];
                keysToDeselect.push(getCatalogRowKey(row));
                removedCount += 1;
                return [];
            });
            if (!nextList.length) {
                setNoResult(true);
            }
            return nextList;
        });

        if (keysToDeselect.length) {
            deselectRowKeys(keysToDeselect);
            setTotalCount((count) => Math.max(0, count - removedCount));
        }
    };

    const applyUpdatedRowToFilteredView = (
        originalRow: AvailsPlusRow,
        updatedRow: AvailsPlusRow
    ) => {
        const rowKey = getCatalogRowKey(originalRow);
        const viewOptions = getActiveViewFilterOptions();
        const matchesView = rowMatchesActiveView(updatedRow, viewOptions);

        if (matchesView) {
            setContentList((prev) =>
                prev.map((item) =>
                    getCatalogRowKey(item) === rowKey ? updatedRow : item
                )
            );
            return;
        }

        setContentList((prev) => {
            const next = prev.filter((item) => getCatalogRowKey(item) !== rowKey);
            if (!next.length) {
                setNoResult(true);
            }
            return next;
        });
        deselectRowKeys([rowKey]);
        setTotalCount((count) => Math.max(0, count - 1));
    };

    const fetchSummary = async (opts?: {
        airtableSourceValue?: AvailsPlusAirtableSourceKey;
    }) => {
        const { airtableSourceValue = airtableSource } = opts ?? {};

        summaryAbortRef.current?.abort();
        const controller = new AbortController();
        summaryAbortRef.current = controller;
        const requestId = ++summaryRequestIdRef.current;

        setSummaryLoading(true);
        try {
            const params = new URLSearchParams({
                airtableSource: airtableSourceValue,
            });

            const res = await axios.get(`${SUMMARY_URL}?${params.toString()}`, {
                signal: controller.signal,
            });
            if (requestId !== summaryRequestIdRef.current) return;
            const data = res.data?.data;
            // Accept `slate` (canonical) or deprecated `overall` alias.
            if ((data?.slate || data?.overall) && data?.syncRequired && data?.expiring) {
                setSummary(data as AvailsPlusSummaryData);
            }
        } catch (error) {
            if (requestId !== summaryRequestIdRef.current) return;
            if (isRequestCanceled(error)) return;
            console.error("Avails+ summary", error);
        } finally {
            if (requestId === summaryRequestIdRef.current) {
                setSummaryLoading(false);
            }
        }
    };

    const fetchSlateSyncStatus = async () => {
        try {
            const res = await axios.get(SLATE_SYNC_STATUS_URL);
            const data = res.data?.data ?? {};
            setSlateSyncEnabled(Boolean(data.enabled));
            setSlatePutEnabled(Boolean(data.putEnabled));
            setSlateSyncMeta(data.meta ?? null);
        } catch (error) {
            // Polled endpoint — network/CORS/auth blips should not spam or break the page.
            if (!isRequestCanceled(error)) {
                console.warn(
                    "Avails+ slate sync status unavailable",
                    error instanceof Error ? error.message : error
                );
            }
        }
    };

    useEffect(() => {
        const shouldPoll = slateSyncMeta?.inProgress || slateSyncMeta?.submitInProgress;
        if (!shouldPoll) return undefined;
        const intervalId = window.setInterval(() => {
            void fetchSlateSyncStatus();
        }, 3000);
        return () => window.clearInterval(intervalId);
    }, [slateSyncMeta?.inProgress, slateSyncMeta?.submitInProgress]);

    const applySlateSubmitResultsToUi = (
        results: Array<{
            refId?: string;
            success?: boolean;
            slateStatus?: SlateStatus;
            mismatches?: SlateMismatch[];
            errors?: Array<{ message?: string; code?: string }>;
        }>
    ) => {
        const nextErrors: Record<string, string> = {};
        const keysToDeselect: string[] = [];
        let removedCount = 0;
        const viewOptions = getActiveViewFilterOptions();

        setContentList((prev) => {
            let nextList = prev;
            for (const result of results) {
                if (!result.refId) continue;
                nextList = nextList.flatMap((row) => {
                    if (row.reference_id !== result.refId) return [row];
                    if (!result.success) {
                        nextErrors[row.reference_id] = formatSlateSubmitError(
                            result.errors
                        );
                        return [row];
                    }
                    const updated: AvailsPlusRow = {
                        ...row,
                        slate_status:
                            (result.slateStatus as SlateStatus) || "synced",
                        slate_mismatches: result.mismatches || [],
                    };
                    if (rowMatchesActiveView(updated, viewOptions)) {
                        return [updated];
                    }
                    keysToDeselect.push(getCatalogRowKey(row));
                    removedCount += 1;
                    return [];
                });
            }
            if (!nextList.length) {
                setNoResult(true);
            }
            return nextList;
        });

        if (keysToDeselect.length) {
            deselectRowKeys(keysToDeselect);
            setTotalCount((count) => Math.max(0, count - removedCount));
        }
        setSlateSyncErrorByRefId(nextErrors);

        if (detailRefId) {
            const detailResult = results.find((item) => item.refId === detailRefId);
            if (detailResult?.success) {
                setDetailSlateStatus((detailResult.slateStatus as SlateStatus) || "synced");
                setDetailSlateMismatches(detailResult.mismatches || []);
            }
        }
    };

    const handleSubmitToSlate = async (refIdsInput: string[]) => {
        if (!slateSyncEnabled || !slatePutEnabled || slateSubmitting) return;

        const refIds = [...new Set(refIdsInput.filter(Boolean))];
        if (!refIds.length) {
            enqueueSnackbar("No titles selected for Slate submit.", {
                variant: "info",
                anchorOrigin: SNACKBAR_ANCHOR,
            });
            return;
        }
        if (refIds.length > SLATE_SUBMIT_MAX_REF_IDS) {
            enqueueSnackbar(
                `Select at most ${SLATE_SUBMIT_MAX_REF_IDS} titles to submit at once.`,
                { variant: "warning", anchorOrigin: SNACKBAR_ANCHOR }
            );
            return;
        }

        setSlateSubmitting(true);
        setSlateSyncingRefId(refIds.length === 1 ? refIds[0] : null);
        try {
            const res = await axios.post(
                `${SLATE_SUBMIT_URL}?airtableSource=${encodeURIComponent(airtableSource)}`,
                { refIds }
            );

            // 202 = bulk background submit — poll until done, then refresh list.
            if (res.status === 202) {
                enqueueSnackbar(
                    `Submitting ${refIds.length} title${refIds.length === 1 ? "" : "s"} to Slate in background…`,
                    { variant: "info", anchorOrigin: SNACKBAR_ANCHOR }
                );
                await fetchSlateSyncStatus();
                // Poll until submitInProgress clears, then refresh list to pick up new slate statuses.
                const poll = window.setInterval(async () => {
                    const statusRes = await axios
                        .get(SLATE_SYNC_STATUS_URL)
                        .catch(() => null);
                    const meta = statusRes?.data?.data?.meta ?? {};
                    setSlateSyncMeta(meta);
                    if (!meta.submitInProgress) {
                        window.clearInterval(poll);
                        setSlateSubmitting(false);
                        setSlateSyncingRefId(null);
                        deselectRefIds(refIds);
                        await fetchList({ skipValue: skip });
                        if (meta.lastSubmitError) {
                            enqueueSnackbar(`Bulk submit completed with errors: ${meta.lastSubmitError}`, {
                                variant: "warning",
                                anchorOrigin: SNACKBAR_ANCHOR,
                            });
                        } else {
                            const succeeded = meta.lastSubmitSucceeded ?? 0;
                            const failed = meta.lastSubmitFailed ?? 0;
                            enqueueSnackbar(
                                failed > 0
                                    ? `Submitted ${succeeded}/${succeeded + failed} to Slate (${failed} failed).`
                                    : `Submitted ${succeeded} title${succeeded === 1 ? "" : "s"} to Slate.`,
                                { variant: failed > 0 ? "warning" : "success", anchorOrigin: SNACKBAR_ANCHOR }
                            );
                        }
                    }
                }, 3000);
                return; // keep slateSubmitting=true until poll resolves
            }

            // 200 = single synchronous submit — apply results immediately.
            const data = res.data?.data ?? {};
            applySlateSubmitResultsToUi(data.results ?? []);
            const summary = summarizeSlateSubmitResults(data);
            enqueueSnackbar(summary.message, {
                variant: summary.variant,
                anchorOrigin: SNACKBAR_ANCHOR,
            });
            await fetchSlateSyncStatus();
            void fetchSummary();
        } catch (error) {
            console.error("Avails+ submit to Slate", error);
            const apiMessage =
                typeof error === "object" &&
                error !== null &&
                "response" in error
                    ? (error as { response?: { data?: { message?: string } } }).response
                          ?.data?.message
                    : undefined;
            enqueueSnackbar(apiMessage || "Failed to submit titles to Slate.", {
                variant: "error",
                anchorOrigin: SNACKBAR_ANCHOR,
            });
        } finally {
            setSlateSubmitting(false);
            setSlateSyncingRefId(null);
        }
    };

    const fetchList = async (opts?: {
        skipValue?: number;
        rowsPerPageValue?: number;
        sortCol?: string | null;
        sortOrd?: SortOrder;
        searchKeyValue?: string;
        filterDateFromValue?: Dayjs | null;
        filterDateToValue?: Dayjs | null;
        columnFiltersValue?: ColumnFiltersState;
        airtableSourceValue?: AvailsPlusAirtableSourceKey;
        widgetSelectionValue?: AvailsWidgetSelection;
        /** When false, skip summary widgets refresh (e.g. pagination / sort-only). Default true. */
        refreshSummary?: boolean;
    }): Promise<AvailsPlusRow[] | undefined> => {
        const {
            skipValue = skip,
            rowsPerPageValue = rowsPerPage,
            sortCol = sortColumn,
            sortOrd = sortOrder,
            searchKeyValue = searchKey,
            filterDateFromValue = filterDateFrom,
            filterDateToValue = filterDateTo,
            columnFiltersValue = columnFilters,
            airtableSourceValue = airtableSource,
            widgetSelectionValue = widgetSelection,
            refreshSummary = true,
        } = opts ?? {};

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const requestId = ++fetchRequestIdRef.current;

        if (!hasLoadedRef.current) {
            setInitialLoading(true);
        } else {
            setPageLoading(true);
        }

        // Catalog-wide tiles (source only) — refresh with list, not per view filters.
        if (refreshSummary) {
            void fetchSummary({ airtableSourceValue });
        }

        try {
            const params = new URLSearchParams({
                searchString: searchKeyValue,
                offset: String(skipValue),
                limit: String(rowsPerPageValue),
            });

            if (sortCol && sortOrd) {
                const sortPrefix = sortOrd === "desc" ? "-" : "";
                params.set("sort", `${sortPrefix}${sortCol}`);
            }

            // Widget filters own date/status params; otherwise use the manual date range.
            if (widgetSelectionValue) {
                appendWidgetFiltersToSearchParams(params, widgetSelectionValue);
            } else {
                if (filterDateFromValue?.isValid()) {
                    params.set(
                        "dateFrom",
                        filterDateFromValue.format("YYYY-MM-DD")
                    );
                }
                const effectiveTo =
                    filterDateToValue?.isValid()
                        ? filterDateToValue
                        : filterDateFromValue;
                if (effectiveTo?.isValid()) {
                    params.set("dateTo", effectiveTo.format("YYYY-MM-DD"));
                }
            }
            appendColumnFiltersToSearchParams(params, columnFiltersValue);
            params.set("airtableSource", airtableSourceValue);

            const searchResult = await axios.get(
                `${SEARCH_URL}?${params.toString()}`,
                {
                    signal: controller.signal,
                }
            );
            if (requestId !== fetchRequestIdRef.current) return;

            hasLoadedRef.current = true;
            const list = (searchResult.data?.data?.list || []).map(
                (row: Record<string, unknown>) => normalizeCatalogRow(row)
            );
            setContentList(list);
            setNoResult(!list.length);
            setTotalCount(searchResult.data?.data?.count || 0);
            void fetchSlateSyncStatus();
            return list;
        } catch (error) {
            if (requestId !== fetchRequestIdRef.current) return;
            if (isRequestCanceled(error)) return;

            console.error("Avails+ fetchList", error);
            enqueueSnackbar("Failed to load Avails+ titles. Please try again.", {
                variant: "error",
                anchorOrigin: SNACKBAR_ANCHOR,
            });
            setContentList([]);
            setTotalCount(0);
            setNoResult(false);
            return [];
        } finally {
            if (requestId === fetchRequestIdRef.current) {
                setInitialLoading(false);
                setPageLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!canViewCatalog) return;
        // Drop test workspace selection when AVAILS_TEST_WORKSPACE is not enabled.
        if (!isAvailsPlusAirtableSourceKey(airtableSource)) {
            setAirtableSource(DEFAULT_AVAILS_PLUS_AIRTABLE_SOURCE);
        }
        if (!isAvailsPlusAirtableSourceKey(previewAirtableSource)) {
            setPreviewAirtableSource(DEFAULT_AVAILS_PLUS_AIRTABLE_SOURCE);
        }
        fetchList({
            airtableSourceValue: isAvailsPlusAirtableSourceKey(airtableSource)
                ? airtableSource
                : DEFAULT_AVAILS_PLUS_AIRTABLE_SOURCE,
        });
        void fetchSlateSyncStatus();
        return () => {
            abortControllerRef.current?.abort();
            summaryAbortRef.current?.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleWidgetSelect = (selection: AvailsWidgetSelection) => {
        setWidgetSelection(selection);
        if (selection) {
            setFilterDateFrom(null);
            setFilterDateTo(null);
            setFilterDateError(null);
        }
        setSkip(AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP);
        clearRowSelection();
        fetchList({
            skipValue: AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP,
            widgetSelectionValue: selection,
            filterDateFromValue: selection ? null : filterDateFrom,
            filterDateToValue: selection ? null : filterDateTo,
        });
    };

    const handleSort = (column: string) => {
        let newSortOrder: SortOrder;
        if (sortColumn === column) {
            if (sortOrder === "asc") newSortOrder = "desc";
            else if (sortOrder === "desc") newSortOrder = null;
            else newSortOrder = "asc";
        } else {
            newSortOrder = "asc";
        }
        setSortColumn(newSortOrder ? column : null);
        setSortOrder(newSortOrder);
        setSkip(AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP);
        clearRowSelection();
        setContentList([]);
        fetchList({
            skipValue: AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP,
            sortCol: newSortOrder ? column : null,
            sortOrd: newSortOrder,
            refreshSummary: false,
        });
    };

    const applyFilters = () => {
        const dateError = validateFilterDateRange(
            filterDateFrom,
            filterDateTo
        );
        if (dateError) {
            setFilterDateError(dateError);
            return;
        }
        setFilterDateError(null);
        setWidgetSelection(null);
        setSkip(AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP);
        clearRowSelection();
        fetchList({
            skipValue: AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP,
            widgetSelectionValue: null,
        });
    };

    const clearFilters = () => {
        setFilterDateFrom(null);
        setFilterDateTo(null);
        setFilterDateError(null);
        setSearchKey("");
        setColumnFilters({});
        setWidgetSelection(null);
        clearRowSelection();
        setSkip(AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP);
        fetchList({
            skipValue: AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP,
            filterDateFromValue: null,
            filterDateToValue: null,
            searchKeyValue: "",
            columnFiltersValue: {},
            widgetSelectionValue: null,
        });
    };

    const handleColumnFilterApply = (
        column: FilterableColumnKey,
        values: string[] | null
    ) => {
        const nextFilters = { ...columnFilters };
        if (!values) {
            delete nextFilters[column];
        } else {
            nextFilters[column] = values;
        }
        setColumnFilters(nextFilters);
        setSkip(AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP);
        clearRowSelection();
        fetchList({
            skipValue: AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP,
            columnFiltersValue: nextFilters,
        });
    };

    const handleCatalogSourceChange = (
        source: AvailsPlusAirtableSourceKey
    ) => {
        setAirtableSource(source);
        setWidgetSelection(null);
        clearRowSelection();
        setSkip(AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP);
        fetchList({
            skipValue: AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP,
            airtableSourceValue: source,
            widgetSelectionValue: null,
        });
    };

    const handleToggleRowKey = (rowKey: string) => {
        setSelectedRowKeys((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    };

    const handleTogglePageSelection = (rowKeys: string[], selected: boolean) => {
        setSelectedRowKeys((prev) => {
            const next = new Set(prev);
            for (const rowKey of rowKeys) {
                if (selected) next.add(rowKey);
                else next.delete(rowKey);
            }
            return next;
        });
    };

    const buildExportPayload = (refIds?: string[], format: "amazon" | "roku" = "amazon") => {
        const body: Record<string, string | string[]> = {
            airtableSource,
            format,
        };
        const trimmedSearch = searchKey.trim();
        if (trimmedSearch) body.searchString = trimmedSearch;
        if (sortColumn && sortOrder) {
            body.sort = `${sortOrder === "desc" ? "-" : ""}${sortColumn}`;
        }
        if (widgetSelection) {
            appendWidgetFiltersToRecord(body, widgetSelection);
        } else {
            if (filterDateFrom?.isValid()) {
                body.dateFrom = filterDateFrom.format("YYYY-MM-DD");
            }
            const effectiveTo = filterDateTo?.isValid()
                ? filterDateTo
                : filterDateFrom;
            if (effectiveTo?.isValid()) {
                body.dateTo = effectiveTo.format("YYYY-MM-DD");
            }
        }
        appendColumnFiltersToRecord(body as Record<string, string>, columnFilters);
        if (refIds?.length) body.refIds = refIds;
        return body;
    };

    const handleExportSpreadsheet = async (
        rowKeys: string[],
        format: "amazon" | "roku" = "amazon"
    ) => {
        if (exportLoading || !rowKeys.length) return;
        if (filterDateError) {
            enqueueSnackbar(filterDateError, {
                variant: "error",
                anchorOrigin: SNACKBAR_ANCHOR,
            });
            return;
        }

        const refIds = refIdsFromRowKeys(rowKeys);
        if (!refIds.length) return;

        setExportLoading(true);
        try {
            const res = await axios.post(
                EXPORT_SPREADSHEET_URL,
                buildExportPayload(refIds, format),
                { responseType: "blob", timeout: ROKU_EXPORT_TIMEOUT_MS }
            );
            const headers: Record<string, string> = {};
            if (res.headers && typeof res.headers === "object") {
                Object.entries(res.headers).forEach(([k, v]) => {
                    if (typeof v === "string") headers[k.toLowerCase()] = v;
                });
            }
            const filename =
                getDownloadFilenameFromHeaders(headers) ||
                `avails-catalog-${dayjs().format("YYYY-MM-DD-HHmm")}.xlsx`;
            downloadBlob(res.data as Blob, filename);
            enqueueSnackbar(
                `Exported ${refIds.length} selected title${refIds.length === 1 ? "" : "s"} (${format === "roku" ? "Roku" : "Amazon"} format).`,
                {
                    variant: "success",
                    anchorOrigin: SNACKBAR_ANCHOR,
                }
            );
        } catch (error) {
            console.error("Avails+ catalog export", error);
            const message =
                (await readExportErrorMessage(error)) ||
                "Failed to export Avails catalog.";
            enqueueSnackbar(message, {
                variant: "error",
                anchorOrigin: SNACKBAR_ANCHOR,
            });
        } finally {
            setExportLoading(false);
        }
    };

    const handleOpenExportMenu = (event: React.MouseEvent<HTMLElement>) => {
        setExportMenuAnchor(event.currentTarget);
    };

    const handleCloseExportMenu = () => {
        setExportMenuAnchor(null);
    };

    const rokuStatusForRef = (refId: string | null, rows: AvailsPlusRow[] = contentList) => {
        if (!refId) return null;
        const match = rows.find((row) => row.reference_id === refId);
        return match?.roku_status ?? null;
    };

    /**
     * Submit to Roku — mirrors Submit to Slate: the backend generates one Roku
     * EMA Avails file for all selected titles behind the scenes and delivers
     * that single file to the Roku bucket, then marks each title Synced.
     */
    const handleSubmitToRoku = async (refIdsInput: string[]) => {
        if (rokuSubmitLoading) return;
        const refIds = [...new Set(refIdsInput.filter(Boolean))];
        if (!refIds.length) {
            enqueueSnackbar("No titles selected for Roku submit.", {
                variant: "info",
                anchorOrigin: SNACKBAR_ANCHOR,
            });
            return;
        }
        if (refIds.length > ROKU_SUBMIT_MAX_REF_IDS) {
            enqueueSnackbar(
                `Select at most ${ROKU_SUBMIT_MAX_REF_IDS} titles to submit at once.`,
                { variant: "warning", anchorOrigin: SNACKBAR_ANCHOR }
            );
            return;
        }

        setRokuSubmitLoading(true);
        setRokuSubmittingRefId(refIds.length === 1 ? refIds[0] : null);
        try {
            const res = await axios.post(
                `${ROKU_SUBMIT_URL}?airtableSource=${encodeURIComponent(airtableSource)}`,
                { refIds },
                { timeout: ROKU_EXPORT_TIMEOUT_MS }
            );
            const data = res.data?.data || {};
            const delivered = Boolean(data.delivered);
            enqueueSnackbar(
                res.data?.message ||
                    (delivered
                        ? `Submitted ${refIds.length} title${refIds.length === 1 ? "" : "s"} to Roku.`
                        : `Stored Roku EMA Avails for ${refIds.length} title${
                              refIds.length === 1 ? "" : "s"
                          } in inventory (Roku delivery disabled).`),
                {
                    variant: delivered ? "success" : "info",
                    anchorOrigin: SNACKBAR_ANCHOR,
                }
            );
            if (activeDetailRef.current.airtableSource !== airtableSource) return;
            if (delivered) {
                const refIdSet = new Set(refIds);
                setContentList((prev) =>
                    prev.map((row) =>
                        refIdSet.has(row.reference_id)
                            ? { ...row, roku_status: "synced" as RokuStatus }
                            : row
                    )
                );
                const activeDetail = activeDetailRef.current;
                if (
                    activeDetail.airtableSource === airtableSource &&
                    activeDetail.refId &&
                    refIdSet.has(activeDetail.refId)
                ) {
                    setDetailRokuStatus("synced");
                }
            }
            void fetchSummary();
            void fetchList({ refreshSummary: false });
        } catch (error) {
            console.error("Avails+ submit to Roku", error);
            const axiosError = error as {
                code?: string;
                message?: string;
                response?: { data?: { message?: string }; status?: number };
            };
            const apiMessage =
                axiosError?.response?.data?.message ||
                (await readExportErrorMessage(error)) ||
                (axiosError?.code === "ECONNABORTED"
                    ? "Roku submit timed out. Please try again."
                    : null) ||
                (axiosError?.message && /network error/i.test(axiosError.message)
                    ? "Roku submit failed (network). The file may still have been delivered — refresh and check Roku status, then retry if needed."
                    : null) ||
                axiosError?.message ||
                "Failed to submit titles to Roku.";
            enqueueSnackbar(apiMessage, {
                variant: "error",
                anchorOrigin: SNACKBAR_ANCHOR,
            });
        } finally {
            setRokuSubmitLoading(false);
            setRokuSubmittingRefId(null);
        }
    };

    const handleOpenDetail = (row: AvailsPlusRow) => {
        setDetailRefId(row.reference_id);
        setDetailSlateStatus(row.slate_status);
        setDetailSlateMismatches(row.slate_mismatches ?? []);
        setDetailRokuStatus(row.roku_status);
        setDetailDrawerOpen(true);
    };

    const handleCloseDetail = () => {
        setDetailDrawerOpen(false);
        setDetailRefId(null);
        setDetailSlateStatus(null);
        setDetailSlateMismatches([]);
        setDetailRokuStatus(null);
    };

    const handleNavigateDetailRef = (refId: string) => {
        setDetailRefId(refId);
        setDetailSlateStatus(null);
        setDetailSlateMismatches([]);
        setDetailRokuStatus(rokuStatusForRef(refId));
    };

    const handleRowUpdate = async (
        row: AvailsPlusRow,
        field: EditableField,
        value: string
    ) => {
        const payload: Record<string, unknown> = {
            window_index: row.window_index,
            version: row.version,
        };
        payload[field] = value;

        try {
            const res = await axios.patch(
                `${UPDATE_URL}/${encodeURIComponent(row.reference_id)}?airtableSource=${encodeURIComponent(airtableSource)}`,
                payload
            );
            const responseData = (res.data?.data ?? {}) as Record<string, unknown>;
            const responseSlateStatus =
                responseData.slate_status ?? responseData.slateStatus;
            const updated = normalizeCatalogRow({
                ...row,
                ...responseData,
                // Prefer freshly recomputed slate from PATCH; never wipe with null.
                slate_status:
                    responseSlateStatus === "synced" ||
                    responseSlateStatus === "require_sync" ||
                    responseSlateStatus === "not_exist"
                        ? responseSlateStatus
                        : row.slate_status,
                slate_mismatches: Array.isArray(responseData.slate_mismatches)
                    ? responseData.slate_mismatches
                    : Array.isArray(responseData.slateMismatches)
                      ? responseData.slateMismatches
                      : row.slate_mismatches,
            });

            if (detailRefId === row.reference_id) {
                setDetailSlateStatus(updated.slate_status);
                setDetailSlateMismatches(updated.slate_mismatches ?? []);
            }

            applyUpdatedRowToFilteredView(row, updated);
            enqueueSnackbar("Catalog row updated.", {
                variant: "success",
                anchorOrigin: SNACKBAR_ANCHOR,
            });
        } catch (error) {
            console.error("Avails+ row update failed", error);
            const apiMessage =
                typeof error === "object" &&
                error !== null &&
                "response" in error &&
                typeof (error as { response?: { data?: { message?: string } } })
                    .response?.data?.message === "string"
                    ? (error as { response: { data: { message: string } } })
                          .response.data.message
                    : null;
            enqueueSnackbar(
                apiMessage ?? "Failed to update catalog row.",
                {
                    variant: "error",
                    anchorOrigin: SNACKBAR_ANCHOR,
                }
            );
            if (
                typeof error === "object" &&
                error !== null &&
                "response" in error &&
                (error as { response?: { data?: { error?: { code?: string } } } })
                    .response?.data?.error?.code === "AVAILS_CATALOG_CONFLICT"
            ) {
                fetchList({ skipValue: skip });
            }
            throw error;
        }
    };

    const validateAirtableDates = (): string | null => {
        if (!airtableStartDate?.isValid()) {
            return "Start date is required.";
        }
        if (airtableEndDate?.isValid()) {
            if (airtableEndDate.isBefore(airtableStartDate, "day")) {
                return "End date cannot be before the start date.";
            }
            const monthsDiff = airtableEndDate.diff(
                airtableStartDate,
                "month",
                true
            );
            if (monthsDiff > MAX_RANGE_MONTHS) {
                return "Date range cannot be larger than 6 months.";
            }
        }
        return null;
    };

    const handleOpenFetchDialog = () => {
        setAirtableStartDate(null);
        setAirtableEndDate(null);
        setFetchError(null);
        setFetchDialogOpen(true);
    };

    const handleCloseFetchDialog = () => {
        setFetchDialogOpen(false);
        setFetchError(null);
    };

    const handleClosePreviewDialog = () => {
        setPreviewDialogOpen(false);
        setPreviewRows([]);
        setPreviewStartDate("");
        setPreviewEndDate("");
        setPreviewAirtableSource(DEFAULT_AVAILS_PLUS_AIRTABLE_SOURCE);
        setPreviewPage(0);
        setPreviewRowsPerPage(PREVIEW_ROWS_PER_PAGE);
    };

    const paginatedPreviewRows = previewRows.slice(
        previewPage * previewRowsPerPage,
        previewPage * previewRowsPerPage + previewRowsPerPage
    );

    const handlePreviewAirtable = async () => {
        const err = validateAirtableDates();
        if (err) {
            setFetchError(err);
            return;
        }
        setFetchError(null);
        if (!airtableStartDate) return;

        const end =
            airtableEndDate?.isValid() ? airtableEndDate : airtableStartDate;
        const startStr = airtableStartDate.format("YYYY-MM-DD");
        const endStr = end.format("YYYY-MM-DD");

        setAirtableLoading(true);
        try {
            const res = await axios.post(PREVIEW_URL, {
                startDate: startStr,
                endDate: endStr,
                airtableSource,
            });
            const list = res?.data?.data?.titles ?? [];
            const rows = (Array.isArray(list) ? list : []).map(normalizePreviewRow);
            setPreviewStartDate(startStr);
            setPreviewEndDate(endStr);
            setPreviewAirtableSource(airtableSource);
            setPreviewRows(rows);
            setPreviewPage(0);
            setPreviewRowsPerPage(PREVIEW_ROWS_PER_PAGE);
            setFetchDialogOpen(false);
            setPreviewDialogOpen(true);
        } catch (e) {
            console.error("Failed to preview Avails+ import", e);
            setFetchError("Failed to fetch titles. Please try again.");
            enqueueSnackbar("Failed to fetch titles from Airtable.", {
                variant: "error",
                anchorOrigin: SNACKBAR_ANCHOR,
            });
        } finally {
            setAirtableLoading(false);
        }
    };

    const handleImportToCanvas = async () => {
        if (!previewStartDate || !previewEndDate) return;

        setImportLoading(true);
        try {
            const res = await axios.post(IMPORT_URL, {
                startDate: previewStartDate,
                endDate: previewEndDate,
                airtableSource: previewAirtableSource,
            });
            const data = res?.data?.data ?? {};
            const message = formatImportToastMessage({
                movies: Number(data.movies) || 0,
                episodes: Number(data.episodes) || 0,
                supplementals: Number(data.supplementals) || 0,
            });
            enqueueSnackbar(message, {
                variant: "success",
                anchorOrigin: SNACKBAR_ANCHOR,
            });
            handleClosePreviewDialog();
            setAirtableSource(previewAirtableSource);
            clearRowSelection();
            fetchList({
                skipValue: skip,
                airtableSourceValue: previewAirtableSource,
            });
        } catch (e) {
            console.error("Failed to import Avails+ catalog", e);
            enqueueSnackbar("Failed to import titles to Canvas.", {
                variant: "error",
                anchorOrigin: SNACKBAR_ANCHOR,
            });
        } finally {
            setImportLoading(false);
        }
    };

    if (!canViewCatalog) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="h6">Avails+</Typography>
                <Typography sx={{ mt: 1 }}>
                    You don&apos;t have access to this module.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
            <div>
                <div
                    className="ingest-header-refresh"
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        gap: 16,
                        marginBottom: 16,
                    }}
                >
                    <Box sx={{ minWidth: 0, flex: "1 1 280px", maxWidth: 560 }}>
                        <Typography variant="body2" sx={{ color: "text.primary" }}>
                            Data from Airtable is synced automatically through the end of
                            the current month. Current: {airtableAutoSyncCurrentDisplay}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ mt: 0.5, fontStyle: "italic", color: "text.secondary" }}
                        >
                            Any dates beyond should be fetch manually by clicking “Fetch
                            titles”
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            flexWrap: "wrap",
                            justifyContent: "flex-end",
                            ml: "auto",
                        }}
                    >
                        {canViewCatalog && (
                            <>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<DownloadIcon fontSize="small" />}
                                    endIcon={<ArrowDropDownIcon fontSize="small" />}
                                    disabled={exportLoading || rokuSubmitLoading}
                                    onClick={handleOpenExportMenu}
                                    sx={{
                                        textTransform: "none",
                                        minHeight: 40,
                                        px: 1.75,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {exportLoading ? "Exporting…" : "Export"}
                                </Button>
                                <Menu
                                    anchorEl={exportMenuAnchor}
                                    open={Boolean(exportMenuAnchor)}
                                    onClose={handleCloseExportMenu}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                >
                                    <MenuItem
                                        disabled={
                                            exportLoading || selectedRowKeys.size === 0
                                        }
                                        onClick={() => {
                                            handleCloseExportMenu();
                                            void handleExportSpreadsheet(
                                                [...selectedRowKeys],
                                                "amazon"
                                            );
                                        }}
                                    >
                                        {selectedRowKeys.size > 0
                                            ? `Export Selected (${selectedRowKeys.size}) - Amazon format`
                                            : "Export Selected - Amazon format"}
                                    </MenuItem>
                                    <MenuItem
                                        disabled={
                                            exportLoading || selectedRowKeys.size === 0
                                        }
                                        onClick={() => {
                                            handleCloseExportMenu();
                                            void handleExportSpreadsheet(
                                                [...selectedRowKeys],
                                                "roku"
                                            );
                                        }}
                                    >
                                        {selectedRowKeys.size > 0
                                            ? `Export Selected (${selectedRowKeys.size}) - Roku Format`
                                            : "Export Selected - Roku Format"}
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                        {canEditCatalog && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "stretch",
                                    gap: 1,
                                    width: 240,
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AirtableLogoIcon />}
                                    onClick={handleOpenFetchDialog}
                                    sx={{
                                        textTransform: "none",
                                        minHeight: 40,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Fetch titles
                                </Button>
                            </Box>
                        )}
                    </Box>
                </div>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <AvailsSummaryWidgets
                        summary={summary}
                        loading={summaryLoading}
                        selection={widgetSelection}
                        onSelect={handleWidgetSelect}
                    />
                    <Box sx={{ mb: 2 }}>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "flex-end",
                                gap: 1.5,
                            }}
                        >
                        <AirtableSourceSelect
                            value={airtableSource}
                            onChange={handleCatalogSourceChange}
                            sx={{
                                width: { xs: "100%", md: 220 },
                                flex: { xs: "1 1 100%", md: "0 0 auto" },
                            }}
                        />
                        <TextField
                            name="searchKey"
                            size="small"
                            label="Search Keyword"
                            placeholder="Title, ref ID, series, season, or licensor"
                            value={searchKey}
                            onChange={(e) => setSearchKey(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") applyFilters();
                            }}
                            InputProps={{
                                endAdornment: (
                                    <>
                                        {searchKey && (
                                            <HighlightOffIcon
                                                className="cursor-pointer"
                                                onClick={() => {
                                                    setSearchKey("");
                                                    setSkip(
                                                        AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP
                                                    );
                                                    clearRowSelection();
                                                    fetchList({
                                                        skipValue:
                                                            AVAILS_PLUS_MANAGEMENT.DEFAULT_SKIP,
                                                        searchKeyValue: "",
                                                    });
                                                }}
                                            />
                                        )}
                                        <SearchIcon
                                            className="cursor-pointer ml-1"
                                            onClick={applyFilters}
                                        />
                                    </>
                                ),
                            }}
                            sx={{
                                width: { xs: "100%", md: 360 },
                                flex: { xs: "1 1 100%", md: "0 0 auto" },
                            }}
                        />
                        <LicenseWindowRangeField
                            dateFrom={filterDateFrom}
                            dateTo={filterDateTo}
                            error={filterDateError}
                            onChange={(from, to) => {
                                setFilterDateFrom(from);
                                setFilterDateTo(to);
                                setFilterDateError(validateFilterDateRange(from, to));
                            }}
                            onApply={applyFilters}
                            sx={{
                                width: { xs: "100%", md: 300 },
                                flex: { xs: "1 1 100%", md: "0 0 auto" },
                            }}
                        />
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                flexShrink: 0,
                                width: { xs: "100%", sm: "auto" },
                            }}
                        >
                            <Button
                                variant="contained"
                                size="small"
                                onClick={applyFilters}
                                sx={{ minWidth: 110, height: 40 }}
                            >
                                
                                Apply filters
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={clearFilters}
                                sx={{ minWidth: 110, height: 40 }}
                            >
                                Clear filters
                            </Button>
                        </Box>
                        </Box>
                        {filterDateError && (
                            <Typography
                                variant="caption"
                                color="error"
                                sx={{ display: "block", mt: 0.75 }}
                            >
                                {filterDateError}
                            </Typography>
                        )}
                        {hasActiveColumnFilters(columnFilters) && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                    gap: 0.75,
                                    mt: 1,
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    Column filters:
                                </Typography>
                                {(Object.keys(FILTERABLE_COLUMN_CONFIG) as FilterableColumnKey[]).map(
                                    (columnKey) => {
                                        if (!isColumnFilterActive(columnKey, columnFilters)) {
                                            return null;
                                        }
                                        const summary = getColumnFilterSummary(
                                            columnKey,
                                            columnFilters
                                        );
                                        const label = FILTERABLE_COLUMN_LABELS[columnKey];
                                        return (
                                            <Chip
                                                key={columnKey}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                label={`${label}: ${summary}`}
                                                onDelete={() =>
                                                    handleColumnFilterApply(columnKey, null)
                                                }
                                            />
                                        );
                                    }
                                )}
                            </Box>
                        )}
                    </Box>

                </LocalizationProvider>

                {initialLoading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 8,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {noResult && !pageLoading && (
                            <Typography variant="h6">No results found</Typography>
                        )}

                        <Box
                            sx={{
                                position: "relative",
                                minHeight: pageLoading ? 280 : undefined,
                            }}
                        >
                            {pageLoading && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "background.paper",
                                        opacity: 0.72,
                                        zIndex: 1,
                                        minHeight: 280,
                                    }}
                                >
                                    <CircularProgress size={36} />
                                </Box>
                            )}
                        <AvailsPlusCatalogTable
                            key={`${sortColumn ?? "none"}-${sortOrder ?? "none"}`}
                            rows={contentList}
                            rowOffset={skip}
                            sortColumn={sortColumn}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                            canEdit={canEditCatalog}
                            onRowUpdate={handleRowUpdate}
                            onRowOpen={handleOpenDetail}
                            columnFilters={columnFilters}
                            onColumnFilterApply={handleColumnFilterApply}
                            selectionEnabled={canViewCatalog}
                            selectedRowKeys={selectedRowKeys}
                            onToggleRowKey={handleToggleRowKey}
                            onTogglePageSelection={handleTogglePageSelection}
                            canSyncToSlate={canEditCatalog && slateSyncEnabled && slatePutEnabled}
                            syncingRefId={slateSyncingRefId}
                            syncErrorByRefId={slateSyncErrorByRefId}
                            onSyncToSlate={(refId) => handleSubmitToSlate([refId])}
                        />
                        </Box>

                        {(showSubmitToSlateFab || canEditCatalog) && selectedRefIds.size > 0 ? (
                            <Box
                                sx={{
                                    position: "fixed",
                                    right: 32,
                                    // Sit above the right-aligned pagination controls
                                    bottom: 88,
                                    zIndex: (theme) => theme.zIndex.drawer - 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-end",
                                    gap: 1,
                                }}
                            >
                                {showSubmitToSlateFab ? (
                                    <Fab
                                        variant="extended"
                                        color="primary"
                                        disabled={selectedRefIds.size === 0 || slateSubmitting}
                                        onClick={() =>
                                            void handleSubmitToSlate([...selectedRefIds])
                                        }
                                    >
                                        {slateSubmitting && !slateSyncingRefId ? (
                                            <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                                        ) : (
                                            <CloudUploadIcon sx={{ mr: 1 }} />
                                        )}
                                        {selectedRefIds.size > 0
                                            ? `Submit to Slate (${selectedRefIds.size})`
                                            : "Submit to Slate"}
                                    </Fab>
                                ) : null}
                                {canEditCatalog ? (
                                    <Fab
                                        variant="extended"
                                        color="primary"
                                        disabled={selectedRefIds.size === 0 || rokuSubmitLoading}
                                        onClick={() =>
                                            void handleSubmitToRoku([...selectedRefIds])
                                        }
                                    >
                                        {rokuSubmitLoading && !rokuSubmittingRefId ? (
                                            <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                                        ) : (
                                            <CloudUploadIcon sx={{ mr: 1 }} />
                                        )}
                                        {selectedRefIds.size > 0
                                            ? `Submit to Roku (${selectedRefIds.size})`
                                            : "Submit to Roku"}
                                    </Fab>
                                ) : null}
                            </Box>
                        ) : null}

                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={Math.floor(skip / rowsPerPage)}
                            onPageChange={(_e, newPage) => {
                                const skipValue = newPage * rowsPerPage;
                                setSkip(skipValue);
                                fetchList({ skipValue, refreshSummary: false });
                            }}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                const limit = Number.parseInt(
                                    e.target.value,
                                    10
                                );
                                setRowsPerPage(limit);
                                setSkip(0);
                                fetchList({
                                    skipValue: 0,
                                    rowsPerPageValue: limit,
                                    refreshSummary: false,
                                });
                            }}
                            rowsPerPageOptions={[25, 50, 100]}
                        />
                    </>
                )}

                <Dialog
                    open={fetchDialogOpen}
                    onClose={handleCloseFetchDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        Fetch titles into Wonder Project
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ mb: 2 }}>
                            Select the Airtable source and license window range. Titles whose
                            playback start falls on or within the selected date(s) will be fetched.
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                <AirtableSourceSelect
                                    value={airtableSource}
                                    onChange={(source: AvailsPlusAirtableSourceKey) => {
                                        handleCatalogSourceChange(source);
                                        setFetchError(null);
                                    }}
                                    fullWidth
                                />
                                <LicenseWindowRangeField
                                    dateFrom={airtableStartDate}
                                    dateTo={airtableEndDate}
                                    error={fetchError}
                                    onChange={(from, to) => {
                                        setAirtableStartDate(from);
                                        setAirtableEndDate(to);
                                        setFetchError(null);
                                    }}
                                    fullWidth
                                />
                                {fetchError && (
                                    <Typography color="error" variant="body2">
                                        {fetchError}
                                    </Typography>
                                )}
                            </Box>
                        </LocalizationProvider>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={handleCloseFetchDialog}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handlePreviewAirtable}
                            disabled={airtableLoading || importLoading}
                        >
                            {airtableLoading ? "Loading…" : "Preview"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={previewDialogOpen}
                    onClose={handleClosePreviewDialog}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>Preview: titles to import</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Review titles below from{" "}
                            <strong>
                                {getAvailsPlusAirtableSourceLabel(
                                    previewAirtableSource
                                )}
                            </strong>
                            . Existing records will be updated; new titles will
                            be added to the catalog.
                            {previewRows.length > 0 &&
                                ` (${previewRows.length} total)`}
                        </Typography>
                        <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={{ maxHeight: 440 }}
                        >
                            <Table
                                stickyHeader
                                size="small"
                                sx={{ tableLayout: "fixed", width: "100%" }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <strong>Ref ID</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Name</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Content type</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Start date</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>End date</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Licensor</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Status</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Window action</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {previewRows.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={8}
                                                align="center"
                                                sx={{ py: 3 }}
                                            >
                                                No titles in this date range.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedPreviewRows.map((row) => (
                                            <PreviewTableRow
                                                key={`${row.refId}-${row.startDate}-${row.endDate}`}
                                                row={row}
                                            />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {previewRows.length > 0 && (
                            <TablePagination
                                component="div"
                                count={previewRows.length}
                                page={previewPage}
                                onPageChange={(_e, newPage) =>
                                    setPreviewPage(newPage)
                                }
                                rowsPerPage={previewRowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    const limit = Number.parseInt(
                                        e.target.value,
                                        10
                                    );
                                    setPreviewRowsPerPage(limit);
                                    setPreviewPage(0);
                                }}
                                rowsPerPageOptions={
                                    PREVIEW_ROWS_PER_PAGE_OPTIONS
                                }
                            />
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={handleClosePreviewDialog}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleImportToCanvas}
                            disabled={
                                !previewStartDate ||
                                !previewEndDate ||
                                importLoading ||
                                previewRows.length === 0
                            }
                        >
                            {importLoading
                                ? "Importing…"
                                : "Import to Canvas"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <AvailsPlusDetailDrawer
                    open={detailDrawerOpen}
                    refId={detailRefId}
                    slateStatus={detailSlateStatus}
                    slateMismatches={detailSlateMismatches}
                    rokuStatus={detailRokuStatus}
                    airtableSource={airtableSource}
                    canEdit={canEditCatalog}
                    canSubmitToSlate={canEditCatalog && slateSyncEnabled && slatePutEnabled}
                    submittingToSlate={
                        slateSubmitting &&
                        Boolean(detailRefId) &&
                        slateSyncingRefId === detailRefId
                    }
                    canSubmitToRoku={canEditCatalog}
                    submittingToRoku={rokuSubmitLoading}
                    onRokuStatusChange={(status) => {
                        setDetailRokuStatus(status);
                        if (!detailRefId || !status) return;
                        setContentList((prev) =>
                            prev.map((row) =>
                                row.reference_id === detailRefId
                                    ? { ...row, roku_status: status }
                                    : row
                            )
                        );
                    }}
                    onClose={handleCloseDetail}
                    onNavigateRef={handleNavigateDetailRef}
                    onUpdated={async () => {
                        const list = await fetchList({ skipValue: skip });
                        if (!detailRefId || !list?.length) return;
                        const match = list.find(
                            (row: AvailsPlusRow) => row.reference_id === detailRefId
                        );
                        if (match?.slate_status) {
                            setDetailSlateStatus(match.slate_status);
                            setDetailSlateMismatches(match.slate_mismatches ?? []);
                        }
                        if (match?.roku_status) {
                            setDetailRokuStatus(match.roku_status);
                        }
                    }}
                    onSlateStatusChange={(status, mismatches) => {
                        if (
                            status !== "synced" &&
                            status !== "require_sync" &&
                            status !== "not_exist"
                        ) {
                            return;
                        }
                        setDetailSlateStatus(status);
                        setDetailSlateMismatches(mismatches);
                        if (!detailRefId) return;
                        applySlateStatusToRefIdInView(
                            detailRefId,
                            status,
                            mismatches
                        );
                    }}
                    onSubmitToSlate={(refId) => handleSubmitToSlate([refId])}
                    onSubmitToRoku={(refId) => handleSubmitToRoku([refId])}
                />
            </div>
        </Box>
    );
};

export default AvailsPlusManagement;
